import React, {
  useState,
  FormEvent,
  ChangeEvent,
  useLayoutEffect,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import type { Swiper as SwiperType } from "swiper";

// Assets
import Assets from "../../core/assets";
import { adminRoutes } from "../../routers/AdminRouters";
import { ResetPasswordFormData } from "../../types";
import { resetAdminPasswordService } from "../../services/admin/adminService.api";

const THEME_STORAGE_KEY = "theme";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams();

  const [formData, setFormData] = useState<ResetPasswordFormData>({
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [errors, setErrors] = useState<Partial<ResetPasswordFormData>>({});
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Theme state
  const [theme, setTheme] = useState<"light" | "dark">("light");

  // helper to sync DOM classes
  const syncDomTheme = (isDark: boolean) => {
    const root = document.documentElement;
    root.classList.toggle("dark", isDark);
    document.body?.classList.toggle("dark", isDark);
  };

  // Initialize theme from localStorage or system preference
  useLayoutEffect(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as
      | "light"
      | "dark"
      | null;
    const systemPrefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;

    const initialTheme: "light" | "dark" =
      stored ?? (systemPrefersDark ? "dark" : "light");
    setTheme(initialTheme);
    syncDomTheme(initialTheme === "dark");
  }, []);

  const applyTheme = (next: "light" | "dark") => {
    setTheme(next);
    localStorage.setItem(THEME_STORAGE_KEY, next);
    syncDomTheme(next === "dark");
  };

  const toggleTheme = () => {
    applyTheme(theme === "dark" ? "light" : "dark");
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ResetPasswordFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ResetPasswordFormData> = {};

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Frontend validation: required + match
    if (!validateForm()) return;

    // Ensure token exists from the reset link
    if (!token) {
      console.error("Missing reset token in URL params");
      alert(
        "Invalid or missing reset token. Please use the latest reset link."
      );
      return;
    }

    setIsLoading(true);
    try {
      const password = formData.newPassword;
      // Call service with (token, password), not as an object
      const success = await resetAdminPasswordService(
        token as string,
        password
      );
      if (success) {
        navigate(adminRoutes.login);
      }
    } catch (err: any) {
      console.error("Reset password failed:", err);
      alert(err?.message || "Reset password failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`${
        theme === "dark" ? "dark " : ""
      }min-h-screen flex bg-surface text-text font-sans overflow-hidden`}
    >
      {/* Left Side - Gradient + Swiper */}
      <div
        className="hidden lg:flex lg:w-1/2 min-h-screen items-center justify-center relative"
        style={{
          background:
            theme === "dark"
              ? "linear-gradient(135deg, #232323 0%, #18191B 60%, #B6CB24 100%)"
              : "#FFFFFF",
        }}
      >
        {/* ...existing swiper code... */}
        <div
          className={`relative z-10 w-[90%] h-[85%] rounded-3xl flex flex-col items-center justify-center shadow-2xl border ${
            theme === "dark"
              ? "bg-black border-[#232323]"
              : "bg-[#F3F4F1] border-gray-200"
          }`}
        >
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={false}
            loop
            autoplay={{ delay: 2000, disableOnInteraction: false }}
            slidesPerView={1}
            onSlideChange={(swiper: SwiperType) =>
              setActiveSlide(swiper.realIndex)
            }
            className="w-full h-full flex flex-col items-center justify-center"
            style={{ background: "transparent" }}
          >
            {[0, 1, 2].map((i) => (
              <SwiperSlide key={i}>
                <div className="flex flex-col items-center justify-center h-full">
                  <img
                    src={
                      theme === "dark"
                        ? Assets.authDarkLogo
                        : Assets.authLightLogo
                    }
                    alt="Gym Stream Logo"
                    className="w-32 h-32 object-contain"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Dots */}
          <div className="flex justify-center space-x-2 mt-8 absolute bottom-8 left-0 right-0">
            {[0, 1, 2].map((idx) => (
              <span
                key={idx}
                className={`w-8 h-1 rounded-full transition-all duration-200 ${
                  activeSlide === idx
                    ? theme === "dark"
                      ? "bg-brand-500"
                      : "bg-[#b6cb24]"
                    : theme === "dark"
                    ? "bg-white/30 border border-brand-500"
                    : "bg-[#e0e1db]"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Reset Password Form */}
      <div
        className={`w-full lg:w-1/2 min-h-screen flex items-center justify-center px-8 py-12 ${
          theme === "dark" ? "bg-surface" : "bg-[#F3F4F1]"
        }`}
      >
        <div className="w-full max-w-md space-y-8 font-sans">
          {/* Theme Toggle */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={`Switch to ${
                theme === "dark" ? "light" : "dark"
              } theme`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              className={`relative p-1 rounded-full transition-all duration-300 flex items-center gap-2 w-24 h-10 ${
                theme === "dark"
                  ? "bg-gray-700 border border-gray-600"
                  : "bg-gray-200 border border-gray-300"
              }`}
            >
              {/* ...existing theme toggle code... */}
              <div
                className={`absolute w-8 h-8 rounded-full transition-all duration-300 flex items-center justify-center ${
                  theme === "dark"
                    ? "translate-x-14 bg-gray-800"
                    : "translate-x-0 bg-[#B6CB24]"
                }`}
              >
                {theme === "dark" ? (
                  <img
                    src={Assets.darkModeIcon}
                    alt="Dark Mode"
                    className="w-4 h-4"
                  />
                ) : (
                  <svg
                    className="w-4 h-4 text-gray-800"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414-1.414zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>

              <span
                className={`absolute left-2 text-xs font-medium transition-opacity duration-300 ${
                  theme === "dark" ? "opacity-100 text-gray-300" : "opacity-0"
                }`}
              >
                DARK
              </span>
              <span
                className={`absolute right-2 text-xs font-medium transition-opacity duration-300 ${
                  theme === "light" ? "opacity-100 text-gray-600" : "opacity-0"
                }`}
              >
                LIGHT
              </span>
            </button>
          </div>

          {/* Header */}
          <div className="text-left">
            <h2
              className={`text-3xl font-bold ${
                theme === "dark" ? "text-text" : "text-gray-900"
              }`}
            >
              Reset Password
            </h2>
            <p
              className={`mt-2 ${
                theme === "dark" ? "text-muted" : "text-gray-600"
              }`}
            >
              Enter your new password below
            </p>
          </div>

          {/* Reset Password Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password Field */}
            <div>
              <label
                htmlFor="newPassword"
                className={`block text-sm font-medium mb-2 ${
                  theme === "dark" ? "text-muted" : "text-gray-700"
                }`}
              >
                New Password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="••••••••••"
                  className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors pr-12 ${
                    theme === "dark"
                      ? `placeholder-muted focus:ring-brand-500 focus:border-transparent bg-card text-text border-border ${
                          errors.newPassword
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`
                      : `placeholder-gray-400 focus:ring-brand-500 focus:border-brand-500 bg-white text-gray-900 border-gray-300 ${
                          errors.newPassword
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((s) => !s)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none ${
                    theme === "dark"
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {showNewPassword ? (
                    <FaEyeSlash className="w-5 h-5" />
                  ) : (
                    <FaEye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className={`block text-sm font-medium mb-2 ${
                  theme === "dark" ? "text-muted" : "text-gray-700"
                }`}
              >
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="••••••••••"
                  className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors pr-12 ${
                    theme === "dark"
                      ? `placeholder-muted focus:ring-brand-500 focus:border-transparent bg-card text-text border-border ${
                          errors.confirmPassword
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`
                      : `placeholder-gray-400 focus:ring-brand-500 focus:border-brand-500 bg-white text-gray-900 border-gray-300 ${
                          errors.confirmPassword
                            ? "border-red-500 focus:ring-red-500"
                            : ""
                        }`
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((s) => !s)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none ${
                    theme === "dark"
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="w-5 h-5" />
                  ) : (
                    <FaEye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex items-center justify-center px-4 py-3 rounded-lg border border-[#C6D92C] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-bold focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-surface ${
                theme === "dark" ? "text-white" : "text-black"
              }`}
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(122, 134, 24, 0.80) 100%)",
              }}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Resetting...
                </div>
              ) : (
                <div className="flex items-center">
                  <img
                    src={Assets.loginArrowIcon}
                    alt="Reset"
                    className={`w-5 h-5 mr-2 ${
                      theme === "light" ? "filter brightness-0" : ""
                    }`}
                  />
                  Reset Password
                </div>
              )}
            </button>

            {/* Back to Login */}
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-brand-500 hover:text-brand-600 transition-colors font-semibold"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
